/**
 * Il tema PrimeVue, ricavato dal marchio invece che copiato a mano.
 *
 * Qui dentro non c'e' **nessun colore letterale**. Ce ne sono due generi di
 * riferimento e nient'altro:
 *
 * - le rampe grezze (`{surface.400}`, `{primary.600}`), che prendono i valori
 *   dagli oggetti di `@bestehr/brand` importati sopra;
 * - le proprieta' custom del marchio (`var(--beste-accent-solid)`), che vengono
 *   da `tokens.css` e cambiano da sole fra tema chiaro e tema scuro.
 *
 * La ragione e' il divieto n. 1 del sistema di progetto — "colori letterali nei
 * componenti, solo token semantici" — e la regola scritta in testa al pacchetto
 * del marchio: se il pannello web e l'app divergono su un colore, e' perche'
 * qualcuno ha scritto un valore a mano, ed e' un difetto da correggere.
 *
 * ## Perche' due meccanismi e non uno
 *
 * PrimeVue 5 esprime la doppia veste con la funzione CSS `light-dark()`: un
 * token vale `light-dark({surface.0}, {surface.900})` e a scegliere e' la
 * proprieta' `color-scheme` dell'elemento. Sono centinaia di token, uno per
 * ogni angolo di ogni componente, e riscriverli tutti sarebbe sia inutile sia
 * fragile.
 *
 * Quindi facciamo due cose distinte.
 *
 * 1. **Sostituiamo le rampe primitive** con quelle di Beste. `surface` diventa
 *    la scala dei neutri caldi, `primary` diventa il bronzo. Da quel momento le
 *    centinaia di token che non tocchiamo affatto — il bordo di una scheda, lo
 *    sfondo di una voce di menu al passaggio del mouse — cadono da sole sui
 *    colori giusti in **entrambi** i temi, perche' la convenzione di Aura
 *    (indice basso = chiaro) coincide con quella della scala Beste.
 *
 *    Non e' una coincidenza fortunata che va verificata a ogni aggiornamento:
 *    e' il motivo per cui la sostituzione delle primitive e' possibile. Se un
 *    giorno Aura invertisse la scala, l'interfaccia diventerebbe illeggibile in
 *    modo cosi' vistoso da non poter passare inosservato.
 *
 * 2. **Riscriviamo i pochi token semantici** in cui il tema Beste dice qualcosa
 *    di diverso da Aura, e li' usiamo le variabili `--beste-*`: sono gia' la
 *    forma corretta in tutti e due i temi, quindi non c'e' nessun
 *    `light-dark()` da comporre e nessun secondo posto in cui il valore possa
 *    divergere.
 *
 * ## Perche' funziona anche se le variabili si annidano
 *
 * `--p-content-border-color: var(--beste-border-default)` e
 * `--beste-border-default` sono dichiarate entrambe sull'elemento radice. Una
 * proprieta' custom risolve i propri `var()` nel valore calcolato
 * dell'elemento dove e' dichiarata: sull'elemento radice vince la regola
 * `[data-beste-theme='dark']` se c'e', altrimenti la media query di sistema.
 * Il risultato e' che cambiare tema resta **un attributo**, senza ridisegnare
 * l'albero dei componenti.
 */

import { palette, radius } from '@bestehr/brand';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

import { t } from './tokens';

/**
 * La scala dei fondali di PrimeVue e' quella dei neutri Beste.
 *
 * Aura chiede gli indici da 0 a 950. La scala del marchio ne ha due in piu' —
 * 25 e 1000 — che restano fuori: 1000 e' il nero del marchio e non e' un
 * fondale d'interfaccia, 25 serve al testo su fondo invertito. Metterli qui
 * significherebbe renderli disponibili a un componente che non li ha chiesti.
 */
const surface = {
  0: palette.neutral[0],
  50: palette.neutral[50],
  100: palette.neutral[100],
  200: palette.neutral[200],
  300: palette.neutral[300],
  400: palette.neutral[400],
  500: palette.neutral[500],
  600: palette.neutral[600],
  700: palette.neutral[700],
  800: palette.neutral[800],
  900: palette.neutral[900],
  950: palette.neutral[950],
} as const;

/**
 * Le quattro tinte di stato mancano del grado 950.
 *
 * La scala Beste si ferma a 900 perche' un grado piu' cupo di quello non ha un
 * uso: sarebbe indistinguibile dal fondale scuro. Aura non chiede mai il 950
 * per verde, ambra e rosso — usa 300, 400 e 600 — quindi la mancanza non
 * lascia buchi. Se un giorno un componente lo chiedesse, il token resterebbe
 * non risolto e comparirebbe come colore mancante: vistoso, che e' il modo in
 * cui vogliamo scoprirlo.
 */
/**
 * I token che Aura ridichiara **per schema di colore**, e che quindi vanno messi li'.
 *
 * ## Il difetto che questo risolve, misurato il 2026-10-13
 *
 * Cosimo ha segnalato due volte in pochi minuti che col tema scuro «rimane lo sfondo bianco»
 * — il modale di un viaggio, poi la creazione di una spedizione. Non erano due pagine: era
 * **tutto** il Cruscotto, e il motivo non e' un colore mancante ma un **livello sbagliato**.
 *
 * Aura dichiara i propri colori due volte: una a `semantic.*`, senza schema, e una dentro
 * `semantic.colorScheme.light` / `.dark`. Noi li sovrascrivevamo **solo al livello senza
 * schema**, e quello di sotto vince. Letto nel pacchetto che girava in produzione:
 *
 *     modal:{background:`{surface.0}`     <- colorScheme.light  di Aura
 *     modal:{background:`{surface.900}`   <- colorScheme.dark   di Aura
 *
 * E `main.ts` tiene `darkModeSelector: false` — scelta giusta e spiegata li', un interruttore
 * solo per il tema — che pero' fa emettere a PrimeVue **il solo schema chiaro**. Quindi
 * `--p-overlay-modal-background` valeva `{surface.0}`, bianco, **in tutt'e due i temi**, e il
 * nostro `t('surface-overlay')` non arrivava mai.
 *
 * I gruppi che Aura ridichiara per schema sono dieci — `surface`, `primary`, `highlight`,
 * `mask`, `formField`, `text`, `content`, `overlay`, `list`, `navigation` — e li
 * sovrascrivevamo **tutti**. Cioe' ogni componente che PrimeVue colora da se' (finestre,
 * tendine, popover, campi dei moduli, elenchi, navigazione) restava chiaro: quel che si vedeva
 * scuro era quel che coloriamo noi con le classi `--beste-*`. Dentro un modale le due cose
 * convivevano, ed e' esattamente la fotografia che Cosimo ha mandato — schede e pastiglie nere
 * dentro un contenitore bianco, con sopra i grigi chiari pensati per il fondo scuro.
 *
 * ## Perche' lo stesso oggetto nei due schemi
 *
 * Perche' **non si cambia nessun colore**: si cambia dove sono scritti. Le `--beste-*` sanno
 * gia' distinguere chiaro e scuro da sole (`:root`, `[data-beste-theme='dark']`, e la media
 * query di sistema), ed e' l'intero motivo per cui `darkModeSelector` e' spento. Dare a Aura
 * lo stesso oggetto per i due schemi vuol dire: qualunque schema tu emetta, vince il nostro —
 * e a decidere il tema resta un posto solo.
 */
const PER_SCHEMA = {
    primary: {
      50: '{bronze.50}',
      100: '{bronze.100}',
      200: '{bronze.200}',
      300: '{bronze.300}',
      400: '{bronze.400}',
      500: '{bronze.500}',
      600: '{bronze.600}',
      700: '{bronze.700}',
      800: '{bronze.800}',
      900: '{bronze.900}',
      950: '{bronze.950}',

      // Qui la polarita' si inverte fra i due temi: in chiaro il riempimento e'
      // bronzo 600 con testo bianco, in scuro e' bronzo 300 con testo quasi
      // nero. Bronzo 600 su fondo neutro 900 fa 2,40:1 — sotto il minimo di 3:1
      // che WCAG 1.4.11 chiede perche' un comando si distingua dal fondo. Il
      // ragionamento sta per esteso in `packages/brand/src/theme.ts`; qui
      // basta pescare i token che ne sono il risultato.
      color: t('accent-solid'),
      contrastColor: t('text-onSolid'),
      hoverColor: t('accent-solidHover'),
      activeColor: t('accent-solidActive'),
    },

    surface,

    /** Righe selezionate, voci evidenziate, risultati di ricerca. */
    highlight: {
      background: t('accent-surface'),
      focusBackground: t('accent-surface'),
      color: t('accent-text'),
      focusColor: t('accent-text'),
    },

    text: {
      color: t('text-primary'),
      hoverColor: t('text-primary'),
      mutedColor: t('text-muted'),
      hoverMutedColor: t('text-secondary'),
    },

    content: {
      borderRadius: `${radius.lg}px`,
      background: t('surface-raised'),
      hoverBackground: t('surface-subtle'),
      borderColor: t('border-default'),
      color: t('text-primary'),
      hoverColor: t('text-primary'),
    },

    /**
     * Il velo dietro le finestre modali.
     *
     * `color` non e' decorazione: PrimeVue lo scrive su `.p-overlay-mask`, e
     * ogni cosa dentro il velo che non si dia un colore suo eredita questo.
     * Fino al 2026-08-18 qui c'era `text-inverse`, che si inverte col tema:
     * nello scuro faceva quasi nero sopra un velo nero, 1,06:1. Non si vedeva
     * perche' oggi nessuna finestra ci appoggia testo direttamente — era una
     * trappola armata, non un difetto visibile. `text-onScrim` non si inverte,
     * e `check-contrast.mjs` lo misura sul velo **composto** sui due fondali
     * possibili invece che sulla tinta che il nome promette.
     */
    mask: {
      transitionDuration: t('duration-slow'),
      background: t('scrim'),
      color: t('text-onScrim'),
    },

    formField: {
      fontWeight: t('font-body-weight'),
      fontSize: t('font-body-size'),

      // 12 px per lato e 8 px sopra e sotto danno un campo alto 40 px: sta
      // dentro la scala a base 4, resta comodo da colpire con il puntatore e
      // lascia respirare la riga di una tabella, che e' il posto in cui questi
      // campi compaiono piu' spesso.
      paddingX: `${12}px`,
      paddingY: `${8}px`,
      sm: { fontSize: t('font-small-size'), paddingX: '8px', paddingY: '4px' },
      lg: { fontSize: t('font-h3-size'), paddingX: '16px', paddingY: '12px' },

      borderRadius: `${radius.md}px`,
      transitionDuration: t('duration-fast'),

      background: t('surface-raised'),
      disabledBackground: t('surface-sunken'),
      filledBackground: t('surface-sunken'),
      filledHoverBackground: t('surface-sunken'),
      filledFocusBackground: t('surface-sunken'),

      // Il bordo di un campo **non e' decorativo**: e' l'unica cosa che dice
      // dove il campo comincia e dove finisce, e WCAG 1.4.11 gli chiede 3:1.
      // Il marchio usa lo stesso grado in tutti e due i temi proprio per
      // questo, invece di schiarirlo in tema scuro come farebbe un bordo
      // ornamentale.
      borderColor: t('border-field'),
      hoverBorderColor: t('border-strong'),
      focusBorderColor: t('accent-solid'),
      invalidBorderColor: t('status-danger-solid'),

      color: t('text-primary'),
      disabledColor: t('text-disabled'),
      placeholderColor: t('text-muted'),
      invalidPlaceholderColor: t('status-danger-text'),
      floatLabelColor: t('text-muted'),
      floatLabelFocusColor: t('accent-text'),
      floatLabelActiveColor: t('text-muted'),
      floatLabelInvalidColor: t('status-danger-text'),
      iconColor: t('text-muted'),

      // Nessuna ombra interna sui campi. Aura ne mette una minuscola che in
      // tema scuro non si vede e in tema chiaro compete con l'unico rilievo
      // che vogliamo far notare, quello delle schede.
      shadow: 'none',
    },

    list: {
      padding: '4px',
      gap: '2px',
      header: { padding: '8px 12px 4px 12px' },
      option: {
        padding: '8px 12px',
        borderRadius: `${radius.sm}px`,
        focusBackground: t('surface-subtle'),
        selectedBackground: '{highlight.background}',
        selectedFocusBackground: '{highlight.focus.background}',
        color: t('text-primary'),
        focusColor: t('text-primary'),
        selectedColor: '{highlight.color}',
        selectedFocusColor: '{highlight.focus.color}',
        icon: { color: t('text-muted'), focusColor: t('text-secondary') },
      },
      optionGroup: {
        padding: '8px 12px',
        background: 'transparent',
        color: t('text-muted'),
      },
    },

    navigation: {
      list: { padding: '4px', gap: '2px' },
      item: {
        padding: '8px 12px',
        borderRadius: `${radius.md}px`,
        gap: '12px',
        focusBackground: t('surface-subtle'),
        activeBackground: t('accent-surface'),
        color: t('text-secondary'),
        focusColor: t('text-primary'),
        activeColor: t('accent-text'),
        icon: {
          size: '18px',
          color: t('text-muted'),
          focusColor: t('text-secondary'),
          activeColor: t('accent-text'),
        },
        transitionDuration: t('duration-fast'),
      },
      submenuLabel: {
        padding: '12px 12px 4px 12px',
        background: 'transparent',
        color: t('text-muted'),
      },
      submenuIcon: {
        color: t('text-muted'),
        focusColor: t('text-secondary'),
        activeColor: t('accent-text'),
      },
    },

    /**
     * Le sovrapposizioni prendono l'ombra dalla scala di profondita' del
     * marchio, che ha quattro livelli e nessun quinto (divieto n. 3).
     *
     * In tema scuro quelle ombre non si vedono, ed e' voluto: li' il rilievo si
     * ottiene schiarendo il fondale — `surface.overlay` e' piu' chiaro di
     * `surface.raised`, che e' piu' chiaro di `surface.canvas` — perche' nero su
     * nero e' il motivo per cui le interfacce scure ricavate per inversione
     * sembrano piatte.
     */
    overlay: {
      select: {
        borderRadius: `${radius.md}px`,
        shadow: t('elevation-overlay'),
        background: t('surface-overlay'),
        borderColor: t('border-default'),
        color: t('text-primary'),
      },
      popover: {
        borderRadius: `${radius.lg}px`,
        padding: '12px',
        shadow: t('elevation-overlay'),
        background: t('surface-overlay'),
        borderColor: t('border-default'),
        color: t('text-primary'),
      },
      modal: {
        borderRadius: `${radius.xl}px`,
        padding: '24px',
        shadow: t('elevation-modal'),
        background: t('surface-overlay'),
        borderColor: t('border-default'),
        color: t('text-primary'),
      },
      navigation: { shadow: t('elevation-overlay') },
    },
} as const;

const preset = definePreset(Aura, {
  primitive: {
    // I raggi vengono dalla stessa tabella che usa l'app: un campo di testo
    // arrotondato in modo diverso fra pannello e telefono e' esattamente il
    // genere di scollamento che questo pacchetto esiste per impedire.
    borderRadius: {
      none: `${radius.none}px`,
      xs: '2px',
      sm: `${radius.sm}px`,
      md: `${radius.md}px`,
      lg: `${radius.lg}px`,
      xl: `${radius.xl}px`,
    },
    bronze: palette.bronze,
    green: palette.green,
    amber: palette.amber,
    red: palette.red,
  },

  semantic: {
    // 15 px e' il corpo del testo del sistema di progetto. PrimeVue lo esprime
    // in `rem` per abitudine; noi restiamo in pixel perche' e' l'unita' in cui
    // e' scritta la scala tipografica condivisa con React Native, e una doppia
    // tabella di conversione e' gia' costata al progetto vecchio titoli di
    // dimensione diversa fra app e pannello. Lo zoom del browser scala i pixel
    // come tutto il resto.
    typography: {
      fontFamily: t('font-sans'),
      fontSize: t('font-body-size'),
      fontWeight: t('font-body-weight'),
      lineHeight: '1.4667', // 22/15: la stessa interlinea, espressa senza unita'
    },

    transitionDuration: t('duration-normal'),
    disabledOpacity: '0.55',
    iconSize: '18px',
    anchorGutter: '4px',

    /**
     * L'anello di focus e' a **due strati** ed e' un requisito, non uno stile.
     *
     * Due pixel di stacco del colore del fondale, poi due pixel d'anello del
     * colore d'accento. Senza lo stacco, l'anello su un bottone gia' pieno
     * d'accento farebbe accento su accento — cioe' 1:1, invisibile — proprio
     * sul comando piu' importante di ogni schermata. Il pacchetto del marchio
     * tiene un token apposta per questo (`border.focusOffset`) e il controllo
     * di contrasto l'ha gia' salvato una volta.
     */
    focusRing: {
      width: '2px',
      style: 'solid',
      color: t('border-focus'),
      offset: '2px',
      shadow: `0 0 0 2px ${t('border-focusOffset')}`,
    },

    /**
     * Tutto quel che Aura ridichiara per schema sta in {@link PER_SCHEMA}, e qui entra nei
     * due schemi con lo stesso oggetto. Scriverlo al livello di sopra non funziona: il
     * livello di sotto lo copre in silenzio. Il perche' per esteso sta su `PER_SCHEMA`.
     */
    colorScheme: {
      light: PER_SCHEMA,
      dark: PER_SCHEMA,
    },
  },
});

export default preset;
